(() => {
  if (window.FourPawAnalytics) return;

  const allowed = (values) => {
    const allowlist = new Set(values);
    return (value) => (allowlist.has(value) ? value : undefined);
  };
  const boolean = (value) => (typeof value === "boolean" ? value : undefined);
  const integer = (value) => {
    const number = Number(value);
    return Number.isInteger(number) && number >= 0 && number <= 1000 ? number : undefined;
  };

  const size = allowed(["XS", "S", "M", "L", "XL"]);
  const color = allowed([
    "aqua",
    "beige",
    "black",
    "gray",
    "orange",
    "pink",
    "red",
    "sky_blue",
  ]);
  const packageKind = allowed(["harness_only", "harness_bungee", "harness_collar", "full_set", "harness_safebelt", "harness_bungee_safebelt", "harness_collar_safebelt", "complete_set"]);
  const accessoryKind = allowed(["leash", "collar", "safebelt"]);
  const sizingV2Fields = {
    weightKg: value => typeof value === "number" && Number.isFinite(value) && value > 0 ? value : undefined,
    weightUnit: allowed(["kg", "lb"]), baseSize: size, finalSize: size,
    refinementOpened: boolean, bodyType: allowed(["", "slim", "average", "broad"]),
    breed: allowed(["", "Akita", "American Pit Bull Terrier", "American Staffordshire Terrier", "Australian Cattle Dog", "Australian Shepherd", "Basset Hound", "Beagle", "Belgian Malinois", "Bernese Mountain Dog", "Bichon Frise", "Border Collie", "Boston Terrier", "Boxer", "Brittany", "English Bulldog", "Cane Corso", "Cavalier King Charles Spaniel", "Cavapoo", "Chihuahua", "Cockapoo", "Cocker Spaniel", "Collie", "Dachshund", "Dalmatian", "Doberman Pinscher", "English Springer Spaniel", "French Bulldog", "German Shepherd", "German Shorthaired Pointer", "Golden Retriever", "Goldendoodle", "Great Dane", "Great Pyrenees", "Greyhound", "Havanese", "Jack Russell Terrier", "Labradoodle", "Labrador Retriever", "Maltese", "Mastiff", "Miniature American Shepherd", "Miniature Dachshund", "Miniature Pinscher", "Miniature Poodle", "Miniature Schnauzer", "Newfoundland", "Papillon", "Pembroke Welsh Corgi", "Pomeranian", "Pug", "Rhodesian Ridgeback", "Rottweiler", "Samoyed", "Shetland Sheepdog", "Shiba Inu", "Shih Tzu", "Siberian Husky", "Staffordshire Bull Terrier", "Standard Poodle", "Toy Poodle", "Vizsla", "Weimaraner", "Whippet", "Yorkshire Terrier", "Mixed Breed"]),
    breedBias: allowed([-1, 0, 1]), wasRefined: boolean,
    reasonCode: allowed(["weight_only", "weight_below_public_range", "weight_above_public_range", "body_slim_down", "body_average_base", "body_broad_up", "breed_down", "breed_up"]),
  };
  const schemas = {
    "fourpaw:product_viewed": {},
    "fourpaw:offer_details_toggled": {open:boolean},
    "fourpaw:gallery_changed": {
      position: integer,
      media_count: integer,
      media_type: allowed(["image", "video", "model", "personalization", "unknown"]),
      source: allowed(["thumbnail", "dot", "arrow", "swipe"]),
      is_personalization_preview: boolean,
    },
    "fourpaw:size_finder_opened": {},
    "fourpaw:size_finder_viewed": sizingV2Fields,
    "fourpaw:size_refinement_opened": sizingV2Fields,
    "fourpaw:size_finder_refined": sizingV2Fields,
    "fourpaw:size_finder_applied": sizingV2Fields,

    "fourpaw:size_finder_calculated": {
      ...sizingV2Fields,
      method: allowed(["weight", "breed"]),
      outcome: allowed(["recommended", "guidance", "needs_input"]),
      recommended_size: size,
      unit: allowed(["lb", "kg"]),
    },
    "fourpaw:size_recommendation_applied": {
      recommended_size: size,
      method: allowed(["weight", "breed"]),
    },
    "fourpaw:variant_option_selected": {
      option_kind: allowed(["color", "size"]),
      option_value: allowed([
        "XS",
        "S",
        "M",
        "L",
        "XL",
        "aqua",
        "beige",
        "black",
        "gray",
        "orange",
        "pink",
        "red",
        "sky_blue",
      ]),
      source: allowed(["customer"]),
    },
    "fourpaw:upsell_interaction": {
      build:allowed(["upsell-v2"]), environment:allowed(["live","preview"]),
      action:allowed(["viewed","details_opened","details_closed","feature_opened","details_end_viewed","color_selected","selected","deselected","cart_added","kit_opened","kit_arrived"]),
      accessory:allowed(["safebelt","bungee","kit","collar"]), slot:allowed([1,2,3]),
      journey:value => typeof value === "string" && /^[a-z0-9]{10,40}$/.test(value) ? value : undefined,
      color:allowed(["Black","Beige","Red","Dark Blue","Pink","Orange","Green","Gray","Aqua","Sky Blue"]),
      source:allowed(["image","title","details","card","popup","kit_upgrade","cart_drawer"]), feature:allowed(["buckle","elastic","clip"]), seconds:integer,
    },
    "fourpaw:package_selected": { package: packageKind },
    "fourpaw:personalization_opened": {},
    "fourpaw:personalization_state_changed": {
      name_present: boolean,
      phone_present: boolean,
    },
    "fourpaw:add_to_cart_attempted": {
      package: packageKind,
      name_present: boolean,
      phone_present: boolean,
      trigger_source: allowed(["main", "sticky"]),
      attempt_number: integer,
    },
    "fourpaw:add_to_cart_accepted": { attempt_number: integer, line_count: integer },
    "fourpaw:cart_sync_failed": { attempt_number: integer },
    "fourpaw:add_to_cart_succeeded": {
      package: packageKind,
      name_present: boolean,
      phone_present: boolean,
      attempt_number: integer,
      line_count: integer,
    },
    "fourpaw:add_to_cart_failed": {
      failure_category: allowed(["validation", "availability", "network", "timeout", "unknown"]),
      attempt_number: integer,
    },
    "fourpaw:checkout_clicked": {
      source: allowed(["drawer", "cart_page"]),
    },
    "fourpaw:cart_upsell_viewed": { accessory: accessoryKind },
    "fourpaw:cart_upsell_attempted": { accessory: accessoryKind },
    "fourpaw:cart_upsell_added": { accessory: accessoryKind },
    "fourpaw:cart_upsell_failed": { accessory: accessoryKind },
  };

  const clarityEventNames = Object.freeze({
    "fourpaw:product_viewed": "FP_ProductViewed",
    "fourpaw:gallery_changed": "FP_GalleryInteraction",
    "fourpaw:size_finder_opened": "FP_SizeFinderOpened",
    "fourpaw:size_finder_calculated": "FP_SizeFinderCalculated",
    "fourpaw:size_finder_viewed": "FP_SizeFinderViewed",
    "fourpaw:size_refinement_opened": "FP_SizeRefinementOpened",
    "fourpaw:size_finder_refined": "FP_SizeFinderRefined",
    "fourpaw:size_finder_applied": "FP_SizeFinderApplied",

    "fourpaw:size_recommendation_applied": "FP_SizeRecommendationApplied",
    "fourpaw:variant_option_selected": "FP_VariantSelected",
    "fourpaw:package_selected": "FP_PackageSelected",
    "fourpaw:personalization_opened": "FP_PersonalizationOpened",
    "fourpaw:personalization_state_changed": "FP_PersonalizationChanged",
    "fourpaw:add_to_cart_attempted": "FP_AddToCartAttempted",
    "fourpaw:add_to_cart_accepted": "FP_AddToCartAccepted",
    "fourpaw:cart_sync_failed": "FP_CartSyncFailed",
    "fourpaw:add_to_cart_succeeded": "FP_AddToCartSucceeded",
    "fourpaw:add_to_cart_failed": "FP_AddToCartFailed",
    "fourpaw:checkout_clicked": "FP_CheckoutClicked",
    "fourpaw:cart_upsell_viewed": "FP_CartUpsellViewed",
    "fourpaw:cart_upsell_attempted": "FP_CartUpsellAttempted",
    "fourpaw:cart_upsell_added": "FP_CartUpsellAdded",
    "fourpaw:cart_upsell_failed": "FP_CartUpsellFailed",
  });

  const clarityQueue = [];
  const clarityRetryLimit = 80;
  const clarityRetryDelay = 250;
  let clarityRetryCount = 0;
  let clarityRetryTimer = null;
  let clarityContextTagged = false;

  const sanitize = (eventName, data = {}) => {
    const schema = schemas[eventName];
    if (!schema) return null;
    const payload = {};
    const arm = document.querySelector("[data-fp-pdp-arm]")?.dataset.fpPdpArm;
    if (["control","challenger"].includes(arm)) payload.pdp_arm = arm;
    Object.entries(schema).forEach(([key, normalize]) => {
      const value = normalize(data[key]);
      if (value !== undefined) payload[key] = value;
    });
    if (eventName === "fourpaw:upsell_interaction" && (!payload.action || !payload.accessory)) return null;
    return payload;
  };

  const clarityContext = () => {
    const country = String(window.Shopify?.country || "unknown").toUpperCase();
    const mode = window.Shopify?.designMode
      ? "editor"
      : window.Shopify?.theme?.role === "unpublished" || new URLSearchParams(window.location.search).has("preview_theme_id")
        ? "preview"
        : "storefront";
    const template = document.querySelector(".shop-product-section fourpaw-package-selector")
      ? "safewalk"
      : document.getElementById("main-cart-footer")
        ? "cart"
        : "other";
    return { country, mode, template };
  };

  const tagClarityContext = () => {
    if (clarityContextTagged || typeof window.clarity !== "function") return;
    const context = clarityContext();
    window.clarity("set", "fourpaw_market", context.country);
    window.clarity("set", "fourpaw_theme_mode", context.mode);
    window.clarity("set", "fourpaw_template", context.template);
    const arm = document.querySelector("[data-fp-pdp-arm]")?.dataset.fpPdpArm;
    if (["control","challenger"].includes(arm)) window.clarity("set","fourpaw_pdp_arm",arm);
    clarityContextTagged = true;
  };

  const scheduleClarityFlush = () => {
    if (clarityRetryTimer || clarityRetryCount >= clarityRetryLimit) return;
    clarityRetryTimer = window.setTimeout(() => {
      clarityRetryTimer = null;
      clarityRetryCount += 1;
      flushClarityQueue();
    }, clarityRetryDelay);
  };

  const flushClarityQueue = () => {
    if (typeof window.clarity !== "function") {
      if (clarityRetryCount >= clarityRetryLimit) clarityQueue.length = 0;
      else scheduleClarityFlush();
      return false;
    }

    try {
      tagClarityContext();
      while (clarityQueue.length) {
        const entry = clarityQueue[0];
        if (typeof entry === "object") {
          Object.entries(entry.tags).forEach(([key,value]) => window.clarity("set", key, String(value)));
          window.clarity("event", entry.name);
        } else window.clarity("event", entry);
        clarityQueue.shift();
      }
      clarityRetryCount = 0;
      return true;
    } catch (_error) {
      if (clarityRetryCount >= clarityRetryLimit) clarityQueue.length = 0;
      else scheduleClarityFlush();
      return false;
    }
  };

  const publishClarity = (eventName, payload = {}) => {
    if (eventName === "fourpaw:offer_details_toggled") { clarityQueue.push(payload.open ? "FP_GiftsDetailsOpened" : "FP_GiftsDetailsClosed"); flushClarityQueue(); return true; }
    if (eventName === "fourpaw:upsell_interaction") {
      const actions = {viewed:"OfferViewed",details_opened:"DetailsOpened",details_closed:"DetailsClosed",feature_opened:"FeatureOpened",details_end_viewed:"DetailsEndViewed",color_selected:"ColorSelected",selected:"Selected",deselected:"Deselected",cart_added:"CartAdded",kit_opened:"KitOpened",kit_arrived:"KitArrived"};
      if (!actions[payload.action] || !["safebelt","bungee","kit","collar"].includes(payload.accessory)) return false;
      const tags = {fourpaw_upsell:payload.accessory,fourpaw_upsell_build:"upsell-v2",fourpaw_upsell_environment:window.Shopify?.theme?.role === "main" ? "live" : "preview"};
      ["source","color","feature","slot","journey"].forEach(key => { if (payload[key] !== undefined) tags["fourpaw_upsell_"+key] = payload[key]; });
      if (payload.seconds !== undefined) tags.fourpaw_upsell_dwell = payload.seconds < 3 ? "0-2s" : payload.seconds < 10 ? "3-9s" : "10s+";
      clarityQueue.push({name:`FP_${payload.accessory === "safebelt" ? "SafeBelt" : payload.accessory === "kit" ? "Kit" : payload.accessory === "collar" ? "Collar" : "Bungee"}${actions[payload.action]}`,tags});
      flushClarityQueue(); return true;
    }
    const clarityEventName = clarityEventNames[eventName];
    if (!clarityEventName) return false;
    clarityQueue.push(clarityEventName);
    flushClarityQueue();
    return true;
  };

  // Isolated behavioral stream. The managed Google app owns commerce measurement.
  const behaviorMeasurementId = "G-0B88CYFEYC";
  let behaviorTag = null;
  let behaviorPrivacyLoadStarted = false;
  const loadBehaviorPrivacyAPI = () => {
    if (behaviorPrivacyLoadStarted || window.location.hostname !== "fourpawco.com" ||
        typeof window.Shopify?.loadFeatures !== "function") return;
    behaviorPrivacyLoadStarted = true;
    try {
      window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.2" }], error => {
        if (!error) syncBehaviorConsent();
      });
    } catch (_error) {
      // Missing privacy infrastructure must leave behavioral measurement off.
    }
  };
  const behaviorConsentAllowed = () => {
    try {
      if (!window.Shopify?.customerPrivacy) loadBehaviorPrivacyAPI();
      return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true;
    } catch (_error) {
      return false;
    }
  };
  const syncBehaviorConsent = () => {
    if (!behaviorTag) return;
    const permitted = behaviorConsentAllowed();
    window[`ga-disable-${behaviorMeasurementId}`] = !permitted;
    behaviorTag("consent", "update", {
      analytics_storage: permitted ? "granted" : "denied",
      ad_storage: "denied", ad_user_data: "denied", ad_personalization: "denied",
    });
  };
  document.addEventListener("visitorConsentCollected", syncBehaviorConsent);
  const googleBehaviorTag = () => {
    if (!behaviorConsentAllowed() || window.location.hostname !== "fourpawco.com") return null;
    if (behaviorTag) return behaviorTag;
    window.fpBehaviorDataLayer = window.fpBehaviorDataLayer || [];
    behaviorTag = function () { window.fpBehaviorDataLayer.push(arguments); };
    behaviorTag("consent", "default", {
      analytics_storage: "denied", ad_storage: "denied",
      ad_user_data: "denied", ad_personalization: "denied",
    });
    behaviorTag("js", new Date());
    syncBehaviorConsent();
    behaviorTag("config", behaviorMeasurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_location: window.location.origin + window.location.pathname,
      page_referrer: "",
    });
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${behaviorMeasurementId}&l=fpBehaviorDataLayer`;
    document.head.appendChild(script);
    return behaviorTag;
  };
  const publishGoogleBehavior = (eventName, payload) => {
    try {
      const tag = googleBehaviorTag();
      if (!tag) return;
      const action = eventName.replace("fourpaw:", "");
      const googleName = eventName === "fourpaw:upsell_interaction" ? "fp_upsell" :
        action.startsWith("cart_upsell_") ? "fp_cart_upsell" :
        action.startsWith("size_") ? "fp_size_finder" :
        action.startsWith("add_to_cart_") || action === "cart_sync_failed" ? "fp_cart_flow" :
        action === "checkout_clicked" ? "fp_checkout_intent" : "fp_product_experience";
      const params = {
        send_to: behaviorMeasurementId,
        page_location: window.location.origin + window.location.pathname,
        page_referrer: "",
        fp_action: payload.action || action,
        fp_environment: clarityContext().mode === "storefront" ? "live" : "preview",
      };
      if (payload.pdp_arm) params.fp_pdp_arm = payload.pdp_arm;
      if (payload.accessory) params.fp_accessory = payload.accessory;
      if (payload.source || payload.trigger_source) params.fp_source = payload.source || payload.trigger_source;
      const offer = document.querySelector(".shop-product-section fourpaw-package-selector");
      if (offer) {
        const order = Array.from(offer.querySelectorAll("[data-package-addon]"))
          .map(node => node.dataset.packageAddon).filter(value => ["bungee", "collar", "safebelt"].includes(value));
        const unique = Array.from(new Set(order));
        if (unique.length) params.fp_offer_order = unique.join("_");
      }
      syncBehaviorConsent();
      tag("event", googleName, params);
    } catch (_error) {
      // Measurement must never interrupt the purchase flow.
    }
  };

  const publish = (eventName, data = {}) => {
    const payload = sanitize(eventName, eventName === "fourpaw:upsell_interaction" ? {...data,build:"upsell-v2",environment:clarityContext().mode === "storefront" ? "live" : "preview"} : data);
    if (!payload) return false;

    try {
      window.Shopify?.analytics?.publish?.(eventName, payload);
    } catch (_error) {
    }

    publishClarity(eventName, payload);
    // Off (Oct 29): G-0B88CYFEYC is a second stream inside the main GA4 property, so it doubled
    // sessions and page views. Re-enable only after moving it to a separate GA4 property.
    // publishGoogleBehavior(eventName, payload);

    document.dispatchEvent(
      new CustomEvent("fourpaw:analytics", {
        detail: { eventName, payload },
      }),
    );
    return true;
  };

  const sectionFor = (element) => element?.closest?.(".shop-product-section") || null;
  const purchaseFlowFor = (element) => {
    const section = sectionFor(element);
    return section?.querySelector("fourpaw-package-selector") ? section : null;
  };
  const selectedPackage = (section) => {
    const includesBungee = Boolean(
      section?.querySelector('[data-package-addon="bungee"] [data-package-input]:checked'),
    );
    const includesCollar = Boolean(
      section?.querySelector('[data-package-addon="collar"] [data-package-input]:checked'),
    );
    const includesSafeBelt = Boolean(section?.querySelector('[data-package-addon="safebelt"] [data-package-input]:checked'));
    if (includesSafeBelt && includesCollar) return includesBungee ? "complete_set" : "harness_collar_safebelt";
    if (includesSafeBelt) return includesBungee ? "harness_bungee_safebelt" : "harness_safebelt";
    if (includesBungee && includesCollar) return "full_set";
    if (includesBungee) return "harness_bungee";
    if (includesCollar) return "harness_collar";
    return "harness_only";
  };
  const personalizationState = (section) => ({
    name_present: Boolean(
      section?.querySelector('[data-fourpaw-personalization-role="dog_name"] input')?.value.trim(),
    ),
    phone_present: Boolean(
      section?.querySelector('[data-fourpaw-personalization-role="phone"] input')?.value.trim(),
    ),
  });

  let attemptNumber = 0;
  let pendingAttempt = null;
  let triggerSource = "main";
  const personalizationStates = new WeakMap();

  const tagClarityTargets = (root = document) => {
    const targets = [
      [".shop-thumbnail, .shop-dot, .shop-indicator-dot, .shop-desktop-prev-arrow, .shop-desktop-next-arrow, .shop-mobile-prev-arrow, .shop-mobile-next-arrow", "gallery"],
      ["[data-size-chart-popup]", "size-finder-open"],
      ["[data-size-calculate], [data-size-breed-calculate]", "size-finder-calculate"],
      ["[data-size-apply]", "size-finder-apply"],
      [".simple-variant-picker__option.option--color .simple-variant-picker__color-swatch", "color-select"],
      [".simple-variant-picker__option.option--size .simple-variant-picker__value.size-value", "size-select"],
      ["[data-package-card]", "package-select"],
      [".fourpaw-personalization-disclosure__toggle", "personalization-open"],
      ["product-form [type='submit']", "add-to-cart"],
      ["[data-package-sticky-button]", "sticky-add-to-cart"],
      ["cart-drawer [name='checkout'], cart-drawer a[href*='/checkout']", "checkout"],
      ["cart-drawer [data-fourpaw-cart-upsell-button]", "cart-upsell-add"],
      ["main [name='checkout'], main a[href*='/checkout']", "checkout"],
    ];
    targets.forEach(([selector, label]) => {
      root.querySelectorAll?.(selector).forEach((element) => {
        element.dataset.fourpawClarityEvent = label;
      });
    });
  };

  const trackSizeCalculation = (button) => {
    const finder = button.closest("fourpaw-size-finder");
    if (!finder || finder.dataset.sizingVersion === "2") return;
    const method = button.matches("[data-size-breed-calculate]") ? "breed" : "weight";
    const recommendedSize = size(String(finder.recommendedSize || "").toUpperCase());
    const hasAnyInput =
      method === "breed"
        ? Boolean(finder.breedSelect?.value)
        : Boolean(finder.weightInput?.value || finder.chestInput?.value);
    publish("fourpaw:size_finder_calculated", {
      method,
      outcome: recommendedSize && finder.canApplyRecommendation ? "recommended" : hasAnyInput ? "guidance" : "needs_input",
      recommended_size: recommendedSize,
      unit: finder.unit,
    });
  };

  const trackVariantSelection = (input, event) => {
    if (!event.isTrusted || !purchaseFlowFor(input)) return;
    const option = input.closest(".simple-variant-picker__option");
    const optionKind = option?.classList.contains("option--size")
      ? "size"
      : option?.classList.contains("option--color")
        ? "color"
        : null;
    if (!optionKind) return;

    const rawValue = String(input.value || "").trim();
    const normalizedValue =
      optionKind === "size"
        ? size(rawValue.toUpperCase())
        : color(rawValue.toLowerCase().replace(/[\s-]+/g, "_"));
    publish("fourpaw:variant_option_selected", {
      option_kind: optionKind,
      option_value: normalizedValue,
      source: "customer",
    });
  };

  document.addEventListener("product-gallery:interaction", (event) => {
    publish("fourpaw:gallery_changed", event.detail || {});
  });

  document.addEventListener("click", (event) => {
    const target = event.target.closest?.("button, a, label");
    if (!target) return;

    if (target.matches("[data-size-chart-popup]")) {
      publish("fourpaw:size_finder_opened");
    }

    if (target.matches("[data-size-calculate], [data-size-breed-calculate]")) {
      window.setTimeout(() => trackSizeCalculation(target), 0);
    }

    if (target.matches("[data-size-apply]")) {
      const finder = target.closest("fourpaw-size-finder");
      publish("fourpaw:size_recommendation_applied", {
        recommended_size: size(String(finder?.recommendedSize || "").toUpperCase()),
        method: finder?.querySelector('[data-size-method][aria-selected="true"]')?.dataset.sizeMethod,
      });
    }

    if (target.matches(".fourpaw-personalization-disclosure__toggle")) {
      window.setTimeout(() => {
        if (target.getAttribute("aria-expanded") === "true") {
          publish("fourpaw:personalization_opened");
        }
      }, 0);
    }

    if (target.matches("[data-package-sticky-button]")) {
      triggerSource = "sticky";
      window.setTimeout(() => {
        triggerSource = "main";
      }, 500);
    }

    const checkout = target.closest("[name='checkout'], a[href*='/checkout']");
    if (checkout) {
      publish("fourpaw:checkout_clicked", {
        source: checkout.closest("cart-drawer") ? "drawer" : "cart_page",
      });
    }
  });

  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter") return;
    const input = event.target.closest?.("[data-size-weight], [data-size-chest], [data-size-breed-search]");
    if (!input) return;
    const finder = input.closest("fourpaw-size-finder");
    const button = input.matches("[data-size-breed-search]")
      ? finder?.querySelector("[data-size-breed-calculate]")
      : finder?.querySelector("[data-size-calculate]");
    if (button) window.setTimeout(() => trackSizeCalculation(button), 0);
  });

  document.addEventListener("change", (event) => {
    const input = event.target;
    if (input.matches?.(".simple-variant-picker__radio, .simple-variant-picker__select")) {
      trackVariantSelection(input, event);
    }
  });

  document.addEventListener("fourpaw:package-change", (event) => {
    publish("fourpaw:package_selected", { package: event.detail?.package });
  });

  document.addEventListener("fourpaw:cart-upsell-viewed", (event) => {
    publish("fourpaw:cart_upsell_viewed", event.detail || {});
  });

  document.addEventListener("fourpaw:cart-upsell-attempted", (event) => {
    publish("fourpaw:cart_upsell_attempted", event.detail || {});
  });

  document.addEventListener("fourpaw:cart-upsell-added", (event) => {
    publish("fourpaw:cart_upsell_added", event.detail || {});
  });

  document.addEventListener("fourpaw:cart-upsell-failed", (event) => {
    publish("fourpaw:cart_upsell_failed", event.detail || {});
  });

  document.addEventListener("focusout", (event) => {
    const wrapper = event.target.closest?.("[data-fourpaw-personalization-role]");
    if (!wrapper) return;
    const section = purchaseFlowFor(wrapper);
    if (!section) return;
    const state = personalizationState(section);
    const stateKey = `${state.name_present}:${state.phone_present}`;
    if (personalizationStates.get(section) === stateKey) return;
    personalizationStates.set(section, stateKey);
    publish("fourpaw:personalization_state_changed", state);
  });

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      const productForm = form.closest?.("product-form");
      const section = purchaseFlowFor(productForm);
      const submitButton = productForm?.querySelector("[type='submit']");
      if (!section || submitButton?.getAttribute("aria-disabled") === "true") return;
      const state = personalizationState(section);
      pendingAttempt = {
        attempt_number: ++attemptNumber,
        package: selectedPackage(section),
        ...state,
      };
      publish("fourpaw:add_to_cart_attempted", {
        ...pendingAttempt,
        trigger_source: triggerSource,
      });
      triggerSource = "main";
    },
    true,
  );

  document.addEventListener("fourpaw:product-form:add-to-cart-accepted", (event) => {
    if (!pendingAttempt || !purchaseFlowFor(event.target)) return;
    publish("fourpaw:add_to_cart_accepted", {
      attempt_number: pendingAttempt.attempt_number, line_count: event.detail?.lineCount,
    });
  });
  document.addEventListener("fourpaw:product-form:cart-sync-failed", (event) => {
    if (!pendingAttempt || !purchaseFlowFor(event.target)) return;
    publish("fourpaw:cart_sync_failed", { attempt_number: pendingAttempt.attempt_number });
    pendingAttempt = null;
  });

  document.addEventListener("fourpaw:product-form:add-to-cart-succeeded", (event) => {
    if (!pendingAttempt || !purchaseFlowFor(event.target)) return;
    publish("fourpaw:add_to_cart_succeeded", {
      ...pendingAttempt,
      line_count: event.detail?.lineCount,
    });
    pendingAttempt = null;
  });

  document.addEventListener("fourpaw:product-form:add-to-cart-failed", (event) => {
    if (!pendingAttempt || !purchaseFlowFor(event.target)) return;
    publish("fourpaw:add_to_cart_failed", {
      failure_category: event.detail?.failureCategory || "unknown",
      attempt_number: pendingAttempt.attempt_number,
    });
    pendingAttempt = null;
  });

  document.addEventListener("shopify:section:load", (event) => tagClarityTargets(event.target));
  tagClarityTargets();

  if (document.querySelector(".shop-product-section fourpaw-package-selector")) {
    publish("fourpaw:product_viewed");
  }

  window.FourPawAnalytics = Object.freeze({
    publish,
    publishClarity,
    sanitize,
    getDiagnostics: () => ({
      clarity_available: typeof window.clarity === "function",
      clarity_queue_length: clarityQueue.length,
      clarity_retry_count: clarityRetryCount,
      clarity_context_tagged: clarityContextTagged,
    }),
  });
})();

